import { useMemo } from 'react';
import {
  Link,
  LoaderFunction,
  Navigate,
  redirect,
  useSearchParams,
} from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/solid';
import { isUSStateCode, USStateId } from '@mosey/utils/constants/us-states';
import { TruncatedList } from '@mosey/components/layout/TruncatedList';
import { TaskCollectionCard } from '@mosey/components/layout/TaskCollectionCard';
import { TaskSummaryCard } from '@mosey/components/layout/Card';
import { QueryFilter } from '@mosey/components/menus/QueryFilter';
import { RobotWithRefreshIcon } from '@mosey/components/Icons';
import { getStatusIconData } from '@mosey/components/layout/StatusIcon';
import { TaskStatus } from '@mosey/api-types';
import { apiBatch } from '../../../utils/fetchApi';
import { TaskRef } from '../utils/types';
import {
  getTaskDueDate,
  isTaskAutomated,
  isTaskDone,
  isTaskInProgress,
  isTaskManaged,
  isTaskOverdue,
  isTaskSetupRelated,
  isTaskTodo,
} from '../utils';
import { TasksOverviewZeroState } from './TasksOverviewZeroState';
import { useLegalEntityRegions, useUser } from '../../../hooks/useUser';
import { TasksOverviewIntegrations } from './TasksOverviewIntegrations';
import { TaskOverviewRegionFilters } from './region-filters/TaskOverviewRegionFilters';
import { useTasksOverviewData } from '../utils/hooks';

export const loader: LoaderFunction = async ({ request }) => {
  const { searchParams } = new URL(request.url);
  const locationId = searchParams.get('location_id');
  const tasksSearchParams = new URLSearchParams();
  let tasksUrl = '/api/compliance/tasks';

  if (locationId) {
    const code = locationId.toUpperCase();

    if (!isUSStateCode(code)) {
      return redirect(`/home`);
    }

    tasksSearchParams.set('region_id', USStateId[code]);
  } else {
    const status = searchParams.get('status');

    if (status === 'managed') {
      tasksSearchParams.set('statuses', TaskStatus.in_progress);
    } else if (status === 'automated' || status === 'done') {
      tasksSearchParams.set(
        'statuses',
        [TaskStatus.done, TaskStatus.in_progress].join(','),
      );
    } else {
      tasksSearchParams.set(
        'statuses',
        [TaskStatus.todo, TaskStatus.in_progress].join(','),
      );
    }

    tasksSearchParams.set('task_type', 'requirement');
  }

  if (tasksSearchParams.size > 0) {
    tasksUrl = `${tasksUrl}?${decodeURIComponent(tasksSearchParams.toString())}`;
  }

  return apiBatch({
    tasks: {
      url: tasksUrl,
      method: 'GET',
    },
    legislation: {
      url: '/api/legislation',
      method: 'GET',
    },
    connections: {
      url: `/api/connections`,
      method: 'GET',
    },
    summary: {
      url: '/api/legal_entity/locations/summary',
      method: 'GET',
    },
  });
};

export const Component = () => {
  const {
    role,
    legal_entity: { is_gutz_enabled: isGutzEnabled },
  } = useUser();
  const regions = useLegalEntityRegions();
  const { tasks, legislation, connections, summary } = useTasksOverviewData();
  const [searchParams] = useSearchParams();
  const locationId = searchParams.get('location_id');
  const status = searchParams.get('status');
  const hasPayrollConnection = connections.some(
    ({ integration: { type } }) => type === 'payroll',
  );

  const {
    mainTasks,
    inProgressTasks,
    assessmentAlerts,
    assessmentTasks,
    setupAlerts,
    setupTasks,
  } = useMemo(() => {
    const mainTasks: TaskRef[] = [];
    const inProgressTasks: TaskRef[] = [];

    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];

      if (isTaskInProgress(task) && !isTaskManaged(task)) {
        inProgressTasks.push(task);
      }

      if (task.source.type === 'requirement' && !isTaskSetupRelated(task)) {
        const { is_assessment: isAssessment, is_setup: isSetup } =
          summary[task.source.location.code];

        switch (status) {
          case 'done':
            if (isTaskDone(task) && !isTaskSetupRelated(task)) {
              mainTasks.push(task);
            }

            break;

          case 'overdue':
            if (isTaskOverdue(task)) {
              mainTasks.push(task);
            }

            break;

          case 'automated':
            if (isTaskAutomated(task)) {
              mainTasks.push(task);
            }

            break;

          case 'managed':
            if (isTaskManaged(task)) {
              mainTasks.push(task);
            }

            break;

          case 'todo':
          case null:
            if (!isAssessment && !isSetup && isTaskTodo(task)) {
              mainTasks.push(task);
            }

            break;
        }
      }
    }

    const assessmentAlerts = locationId
      ? []
      : regions.filter((region) => summary[region.code].is_assessment);
    const setupAlerts = locationId
      ? []
      : regions.filter((region) => summary[region.code].is_setup);

    const assessmentTasks = locationId
      ? tasks.filter(
          (task) => task.source.type === 'question' && isTaskSetupRelated(task),
        )
      : [];

    const setupTasks = locationId
      ? tasks.filter(
          (task) =>
            task.source.type === 'requirement' && isTaskSetupRelated(task),
        )
      : [];

    return {
      mainTasks,
      inProgressTasks,
      assessmentAlerts,
      assessmentTasks,
      setupAlerts,
      setupTasks,
    };
  }, [tasks, regions, summary, locationId, status]);

  const filteredLegislation = legislation.filter(({ region }) => {
    if (locationId && region.code.toLowerCase() !== locationId) {
      return false;
    }

    return true;
  });

  if (role !== 'admin' || !isGutzEnabled) {
    return <Navigate to="/home" />;
  }

  let todoCount = 0;
  let overdueCount = 0;
  let doneCount = 0;
  let automatedCount = 0;
  let managedCount = 0;

  if (locationId) {
    ({
      todo_count: todoCount,
      overdue_count: overdueCount,
      done_count: doneCount,
      automated_count: automatedCount,
      managed_count: managedCount,
    } = summary[locationId.toUpperCase()]);
  } else {
    for (let i = 0; i < regions.length; i++) {
      const region = regions[i];
      const locationSummary = summary[region.code];

      todoCount += locationSummary.todo_count;
      overdueCount += locationSummary.overdue_count;
      doneCount += locationSummary.done_count;
      automatedCount += locationSummary.automated_count;
      managedCount += locationSummary.managed_count;
    }
  }

  return (
    <div className="w-full max-w-full">
      <TaskOverviewRegionFilters />

      <div className="items-start space-y-10 px-8 pb-6 lg:grid lg:grid-cols-[minmax(0,_1fr)_374px] lg:gap-x-10 lg:space-y-0 ">
        <div>
          <QueryFilter
            queryKey="status"
            options={[
              {
                label: 'To do',
                value: 'todo',
                count: todoCount,
                icon: getStatusIconData('new'),
              },
              {
                label: 'Overdue',
                value: 'overdue',
                count: overdueCount,
                icon: getStatusIconData('overdue'),
              },
              {
                label: 'Done',
                value: 'done',
                count: doneCount,
                icon: {
                  Component: CheckCircleIcon,
                  baseColor: 'text-teal-700',
                },
              },
              {
                label: 'Automated',
                value: 'automated',
                count: automatedCount,
                icon: getStatusIconData('automated'),
              },
              {
                label: 'Managed',
                value: 'managed',
                count: managedCount,
                icon: {
                  Component: RobotWithRefreshIcon,
                  baseColor: 'text-violet-600',
                },
              },
            ]}
          />

          {mainTasks.length > 0 ? (
            <ul aria-live="polite" className="space-y-2">
              {mainTasks.map((task) => {
                const { id, status, title, source } = task;
                const formattedDueDate = getTaskDueDate(task);

                return (
                  <TaskSummaryCard
                    as="li"
                    key={id}
                    status={isTaskOverdue(task) ? 'overdue' : status}
                    title={title}
                    description={
                      source.type === 'requirement'
                        ? source.description
                        : undefined
                    }
                    reason={
                      formattedDueDate
                        ? { title: formattedDueDate, type: 'due' }
                        : undefined
                    }
                    to={
                      source.type === 'question'
                        ? `/resolver/question/tasks/${id}`
                        : `/requirement/${id}`
                    }
                    region={
                      source.type === 'requirement'
                        ? source.location.name
                        : source.locations[0].name
                    }
                  />
                );
              })}
            </ul>
          ) : (
            <TasksOverviewZeroState />
          )}
        </div>

        <aside className="isolate space-y-7">
          {!hasPayrollConnection && <TasksOverviewIntegrations />}

          <TruncatedList
            title="Complete Setup"
            body="Setup tasks are based on your responses to questions"
            itemLabel={locationId ? 'tasks' : 'locations'}
            as="nav"
            aria-live="polite"
          >
            {locationId
              ? setupTasks.map(({ id, title, status }) => (
                  <TaskSummaryCard
                    key={id}
                    as="li"
                    title={title}
                    status={status}
                    variant="nav"
                    to={`/requirement/${id}`}
                  />
                ))
              : setupAlerts.map((region) => (
                  <TaskCollectionCard
                    key={region.code}
                    as="li"
                    title={region.name}
                    status="setup-collection"
                    size="medium"
                    to={`/locations/usa/${region.code.toLowerCase()}/setup`}
                    ariaLabelTitle={`${region.name} setup`}
                  />
                ))}
          </TruncatedList>

          <TruncatedList
            title="Answer Questions"
            body="Your responses generate setup tasks"
            itemLabel={locationId ? 'tasks' : 'locations'}
            as="nav"
            aria-live="polite"
          >
            {locationId
              ? assessmentTasks.map(({ id, title, status }) => {
                  return (
                    <TaskSummaryCard
                      key={id}
                      as="li"
                      title={title}
                      status={status}
                      variant="nav"
                      to={`/locations/usa/${locationId}/resolver/assessment/tasks/${id}`}
                    />
                  );
                })
              : assessmentAlerts.map((region) => {
                  return (
                    <TaskCollectionCard
                      key={region.code}
                      as="li"
                      title={region.name}
                      status="question-collection"
                      size="medium"
                      to={`/locations/usa/${region.code.toLowerCase()}/resolver/assessment`}
                      ariaLabelTitle={`${region.name} assessment`}
                    />
                  );
                })}
          </TruncatedList>

          <TruncatedList
            title="Automations in Progress"
            body="You will be notified if action is required"
            itemLabel="automations"
            isStacked
            as="nav"
            aria-live="polite"
          >
            {inProgressTasks.map(({ id, title, status }) => {
              return (
                <TaskSummaryCard
                  key={id}
                  as="li"
                  title={title}
                  status={status}
                  variant="nav"
                  to={`/requirement/${id}`}
                />
              );
            })}
          </TruncatedList>

          <TruncatedList
            title="Legislation"
            body="Tasks will be generated for legislation that applies to you"
            itemLabel="updates"
            isStacked
            as="nav"
            aria-live="polite"
          >
            {filteredLegislation.map(
              ({ id, title, effective_date: effectiveDate }) => {
                return (
                  <Link
                    key={id}
                    className="relative z-0 -mt-px block space-y-0.5 border border-gray-200 px-3 py-[10px] text-sm outline-none last:border-b hover:z-10 hover:border-b hover:border-teal-300 hover:bg-teal-200 focus-visible:z-20 focus-visible:ring-2 focus-visible:ring-rose-700"
                    to={`/legislation#${id}`}
                  >
                    <p className="font-medium">{title}</p>
                    {effectiveDate && <p>Effective {effectiveDate}</p>}
                  </Link>
                );
              },
            )}
          </TruncatedList>

          {hasPayrollConnection && <TasksOverviewIntegrations />}
        </aside>
      </div>
    </div>
  );
};
